const UNIT_SELECTOR = '.fishy-applied-query-unit[data-query-has-cue="true"]';
const AND_GROUP_SELECTOR = '.fishy-applied-and-group-cue[data-query-and-group="true"]';
const ANCHOR_SELECTOR = ".fishy-applied-query-range-anchor";
const CUE_SELECTOR = ".fishy-applied-query-unit-effect";
const CONNECTOR_SELECTOR = ".fishy-applied-query-connector";

const CONTENT_WIDTH_PROPERTY = "--fishymap-query-ribbon-content-width";
const CUE_OFFSET_PROPERTY = "--fishymap-query-ribbon-cue-offset";
const CONNECTOR_LEFT_PROPERTY = "--fishymap-query-ribbon-connector-left";
const CONNECTOR_WIDTH_PROPERTY = "--fishymap-query-ribbon-connector-width";
const AND_GROUP_START_OFFSET_PROPERTY = "--fishymap-query-and-group-start-offset";
const AND_GROUP_END_OFFSET_PROPERTY = "--fishymap-query-and-group-end-offset";

const DEFAULT_GAP_PX = 4;
const DEFAULT_TRAILING_PADDING_PX = 8;
const ORDER_EPSILON_PX = 0.001;

function nonNegativeNumber(value, label) {
  const number = Number(value);
  if (!Number.isFinite(number) || number < 0) {
    throw new RangeError(`${label} must be a finite non-negative number`);
  }
  return number;
}

function positiveNumber(value, label) {
  const number = Number(value);
  if (!Number.isFinite(number) || number <= 0) {
    throw new RangeError(`${label} must be a finite positive number`);
  }
  return number;
}

/**
 * Centers intrinsic-width cues on their fixed source anchors. Colliding cues
 * form minimum-displacement ordered clusters, so an isolated neighbor keeps
 * its natural centered position.
 */
export function packQueryRibbonIntervals(intervals, options = {}) {
  if (!Array.isArray(intervals)) {
    throw new TypeError("intervals must be an array");
  }
  const gap = nonNegativeNumber(options.gap ?? DEFAULT_GAP_PX, "gap");
  const minimumContentWidth = nonNegativeNumber(
    options.minimumContentWidth ?? 0,
    "minimumContentWidth",
  );
  const trailingPadding = nonNegativeNumber(
    options.trailingPadding ?? DEFAULT_TRAILING_PADDING_PX,
    "trailingPadding",
  );
  const normalized = intervals.map((interval, index) => {
    if (!interval || typeof interval !== "object" || Array.isArray(interval)) {
      throw new TypeError(`intervals[${index}] must be an object`);
    }
    const anchorStart = nonNegativeNumber(
      interval.anchorStart,
      `intervals[${index}].anchorStart`,
    );
    const anchorEnd = positiveNumber(interval.anchorEnd, `intervals[${index}].anchorEnd`);
    if (anchorEnd <= anchorStart) {
      throw new RangeError(`intervals[${index}].anchorEnd must be greater than anchorStart`);
    }
    return {
      id: interval.id ?? index,
      anchorStart,
      anchorEnd,
      cueWidth: nonNegativeNumber(interval.cueWidth, `intervals[${index}].cueWidth`),
    };
  });

  for (let index = 1; index < normalized.length; index += 1) {
    if (normalized[index].anchorStart + ORDER_EPSILON_PX < normalized[index - 1].anchorEnd) {
      throw new RangeError("intervals must be in non-overlapping source order");
    }
  }

  const prefixes = [];
  const preferred = normalized.map((interval) => {
    const anchorCenter = (interval.anchorStart + interval.anchorEnd) / 2;
    return {
      ...interval,
      anchorCenter,
      preferredCueStart: anchorCenter - interval.cueWidth / 2,
    };
  });

  // With cueStart[i] = fitted[i] + prefix[i], the ordered gap constraint is
  // exactly fitted[i] <= fitted[i + 1]. Pool-adjacent-violators therefore
  // gives the least-squares displacement from every centered preference.
  const blocks = [];
  let prefix = 0;
  for (let index = 0; index < preferred.length; index += 1) {
    prefixes.push(prefix);
    blocks.push({
      first: index,
      last: index,
      count: 1,
      mean: preferred[index].preferredCueStart - prefix,
    });
    while (
      blocks.length > 1 &&
      blocks[blocks.length - 2].mean > blocks[blocks.length - 1].mean
    ) {
      const right = blocks.pop();
      const left = blocks.pop();
      const count = left.count + right.count;
      blocks.push({
        first: left.first,
        last: right.last,
        count,
        mean: (left.mean * left.count + right.mean * right.count) / count,
      });
    }
    prefix += preferred[index].cueWidth + gap;
  }

  const fitted = Array(preferred.length);
  for (const block of blocks) {
    // The first cue is the only physical leading-edge constraint. Because the
    // fitted values are nondecreasing, this lower bound propagates exactly as
    // far as collision avoidance requires and never clamps the trailing edge.
    const value = Math.max(0, block.mean);
    for (let index = block.first; index <= block.last; index += 1) {
      fitted[index] = value;
    }
  }

  const placements = preferred.map((interval, index) => {
    const cueStart = fitted[index] + prefixes[index];
    const cueEnd = cueStart + interval.cueWidth;
    const cueCenter = (cueStart + cueEnd) / 2;
    const cueOffset = cueStart - interval.anchorStart;
    return {
      ...interval,
      cueStart,
      cueEnd,
      cueCenter,
      cueOffset,
      shifted: Math.abs(cueStart - interval.preferredCueStart) > ORDER_EPSILON_PX,
      detached:
        cueEnd < interval.anchorStart - ORDER_EPSILON_PX ||
        cueStart > interval.anchorEnd + ORDER_EPSILON_PX,
    };
  });
  const sourceEnd = normalized.reduce(
    (maximum, interval) => Math.max(maximum, interval.anchorEnd),
    0,
  );
  const cueEnd = placements.reduce(
    (maximum, placement) => Math.max(maximum, placement.cueEnd),
    0,
  );
  return {
    placements,
    sourceEnd,
    contentWidth: Math.max(
      minimumContentWidth,
      sourceEnd + trailingPadding,
      cueEnd + trailingPadding,
    ),
    gap,
    trailingPadding,
  };
}

/**
 * Places an endpoint-tangent connector from the source-anchor center to the
 * cue's top-center. Displaced connectors spend only one circular quarter-turn
 * at each end and keep all remaining distance as a straight horizontal span.
 */
export function queryRibbonConnectorGeometry(placement, connectorHeight) {
  if (!placement || typeof placement !== "object" || Array.isArray(placement)) {
    throw new TypeError("placement must be an object");
  }
  const anchorStart = nonNegativeNumber(placement.anchorStart, "placement.anchorStart");
  const anchorCenter = nonNegativeNumber(placement.anchorCenter, "placement.anchorCenter");
  const cueStart = nonNegativeNumber(placement.cueStart, "placement.cueStart");
  const cueWidth = nonNegativeNumber(placement.cueWidth, "placement.cueWidth");
  const height = rounded(positiveNumber(connectorHeight, "connectorHeight"));
  const cueCenter = cueStart + cueWidth / 2;
  const displacement = cueCenter - anchorCenter;

  if (Math.abs(displacement) <= ORDER_EPSILON_PX) {
    return {
      left: anchorCenter - anchorStart - 0.5,
      width: 1,
      viewBox: `0 0 1 ${height}`,
      path: `M 0.5 0 V ${height}`,
      direction: "center",
    };
  }
  const movesRight = displacement > 0;
  const width = rounded(Math.abs(displacement));
  const middle = roundedSvg(height / 2);
  const radius = roundedSvg(Math.min(middle, width / 2));
  const beforeTurn = roundedSvg(middle - radius);
  const afterTurn = roundedSvg(middle + radius);
  const nearEdge = radius;
  const farEdge = roundedSvg(width - radius);
  const rightPath = [
    "M 0 0",
    `V ${beforeTurn}`,
    `A ${radius} ${radius} 0 0 0 ${nearEdge} ${middle}`,
    `H ${farEdge}`,
    `A ${radius} ${radius} 0 0 1 ${width} ${afterTurn}`,
    `V ${height}`,
  ].join(" ");
  const leftPath = [
    `M ${width} 0`,
    `V ${beforeTurn}`,
    `A ${radius} ${radius} 0 0 1 ${farEdge} ${middle}`,
    `H ${nearEdge}`,
    `A ${radius} ${radius} 0 0 0 0 ${afterTurn}`,
    `V ${height}`,
  ].join(" ");
  return {
    left: Math.min(anchorCenter, cueCenter) - anchorStart,
    width,
    viewBox: `0 0 ${width} ${height}`,
    path: movesRight ? rightPath : leftPath,
    direction: movesRight ? "right" : "left",
  };
}

function elementRect(element, label, { allowHidden = false, vertical = false } = {}) {
  if (!element || typeof element.getBoundingClientRect !== "function") {
    throw new TypeError(`${label} must provide getBoundingClientRect()`);
  }
  const rect = element.getBoundingClientRect();
  const left = Number(rect?.left);
  const right = Number(rect?.right);
  const width = Number(rect?.width);
  if (allowHidden && [left, right, width].every(Number.isFinite) && width === 0 && right === left) {
    return null;
  }
  if (![left, right, width].every(Number.isFinite) || right <= left || width <= 0) {
    throw new RangeError(`${label} must have positive horizontal geometry`);
  }
  if (!vertical) {
    return { left, right, width };
  }
  const top = Number(rect?.top);
  const bottom = Number(rect?.bottom);
  if (![top, bottom].every(Number.isFinite) || bottom < top) {
    throw new RangeError(`${label} must have finite vertical geometry`);
  }
  return { left, right, width, top, bottom };
}

function writableElement(element, label) {
  if (
    !element?.style ||
    typeof element.style.setProperty !== "function" ||
    typeof element.setAttribute !== "function"
  ) {
    throw new TypeError(`${label} must provide writable style and attributes`);
  }
  return element;
}

function writableAttributeElement(element, label) {
  if (!element || typeof element.setAttribute !== "function") {
    throw new TypeError(`${label} must provide setAttribute()`);
  }
  return element;
}

function rounded(value) {
  const result = Math.round(value * 1_000) / 1_000;
  return Object.is(result, -0) ? 0 : result;
}

function roundedSvg(value) {
  const result = Math.round(value * 10_000) / 10_000;
  return Object.is(result, -0) ? 0 : result;
}

function cssPixels(value) {
  return `${rounded(value)}px`;
}

function expressionPathBelongsToGroup(path, groupPath) {
  return path === groupPath ||
    path.startsWith(`${groupPath}.`) ||
    path.startsWith(`${groupPath}::`);
}

/**
 * Measures and lays out the current ribbon synchronously. It owns no timers,
 * observers, events, or interaction state.
 */
export function layoutQueryRibbon(rail) {
  if (!rail || typeof rail.querySelectorAll !== "function") {
    throw new TypeError("rail must provide querySelectorAll()");
  }
  writableElement(rail, "rail");
  const railRect = elementRect(rail, "rail", { allowHidden: true });
  if (!railRect) {
    return null;
  }
  const scrollLeft = nonNegativeNumber(rail.scrollLeft ?? 0, "rail.scrollLeft");
  const clientLeft = nonNegativeNumber(rail.clientLeft ?? 0, "rail.clientLeft");
  const contentOrigin = railRect.left + clientLeft - scrollLeft;
  const groups = Array.from(rail.querySelectorAll(AND_GROUP_SELECTOR)).map((group, index) => {
    const path = String(group?.getAttribute?.("data-expression-path") ?? "").trim();
    if (!path) {
      throw new RangeError(`AND group ${index} must provide an expression path`);
    }
    return {
      element: writableElement(group, `AND group ${index}`),
      rect: elementRect(group, `AND group ${index}`),
      path,
    };
  });

  // Validate every measurement before mutating the DOM.
  const measured = Array.from(rail.querySelectorAll(UNIT_SELECTOR)).map((unit, index) => {
    if (!unit || typeof unit.querySelector !== "function") {
      throw new TypeError(`unit ${index} must provide querySelector()`);
    }
    const anchorRect = elementRect(
      unit.querySelector(ANCHOR_SELECTOR),
      `anchor ${index}`,
      { vertical: true },
    );
    const cue = writableElement(unit.querySelector(CUE_SELECTOR), `cue ${index}`);
    const cueRect = elementRect(cue, `cue ${index}`, { vertical: true });
    const connector = writableElement(
      unit.querySelector(CONNECTOR_SELECTOR),
      `connector ${index}`,
    );
    const connectorPath = writableAttributeElement(
      connector.querySelector?.("path"),
      `connector path ${index}`,
    );
    return {
      id: unit.getAttribute?.("data-expression-path") || index,
      cue,
      connector,
      connectorPath,
      anchorStart: anchorRect.left - contentOrigin,
      anchorEnd: anchorRect.right - contentOrigin,
      cueWidth: cueRect.width,
      connectorHeight: positiveNumber(
        cueRect.top - anchorRect.bottom,
        `connector ${index} vertical span`,
      ),
    };
  });
  // CSS owns the live viewport minimum via max(100%, <intrinsic width>), so
  // this value stays valid when the container resizes without a relayout.
  const packed = packQueryRibbonIntervals(measured);
  const groupLayouts = groups.map((group, index) => {
    const placements = packed.placements.filter((_placement, placementIndex) => {
      return expressionPathBelongsToGroup(String(measured[placementIndex].id), group.path);
    });
    if (!placements.length) {
      throw new RangeError(`AND group ${index} must contain a rendered cue`);
    }
    const visualStart = placements.reduce(
      (minimum, placement) => Math.min(minimum, placement.cueStart),
      placements[0].cueStart,
    );
    const visualEnd = placements.reduce(
      (maximum, placement) => Math.max(maximum, placement.cueEnd),
      placements[0].cueEnd,
    );
    const groupStart = group.rect.left - contentOrigin;
    const groupEnd = group.rect.right - contentOrigin;
    return {
      ...group,
      visualStart,
      visualEnd,
      startOffset: visualStart - groupStart,
      endOffset: groupEnd - visualEnd,
    };
  });

  rail.style.setProperty(CONTENT_WIDTH_PROPERTY, cssPixels(packed.contentWidth));
  rail.setAttribute("data-query-ribbon-content-width", String(rounded(packed.contentWidth)));
  packed.placements.forEach((placement, index) => {
    const { cue, connector, connectorPath, connectorHeight } = measured[index];
    const connectorGeometry = queryRibbonConnectorGeometry(placement, connectorHeight);
    cue.style.setProperty(CUE_OFFSET_PROPERTY, cssPixels(placement.cueOffset));
    connector.style.setProperty(CONNECTOR_LEFT_PROPERTY, cssPixels(connectorGeometry.left));
    connector.style.setProperty(CONNECTOR_WIDTH_PROPERTY, cssPixels(connectorGeometry.width));
    connector.setAttribute("data-query-connector-direction", connectorGeometry.direction);
    connector.setAttribute("viewBox", connectorGeometry.viewBox);
    connectorPath.setAttribute("d", connectorGeometry.path);
    cue.setAttribute("data-query-ribbon-cue-start", String(rounded(placement.cueStart)));
    cue.setAttribute("data-query-ribbon-cue-end", String(rounded(placement.cueEnd)));
    cue.setAttribute("data-query-ribbon-shifted", String(placement.shifted));
    cue.setAttribute("data-query-ribbon-detached", String(placement.detached));
  });
  groupLayouts.forEach((group) => {
    group.element.style.setProperty(
      AND_GROUP_START_OFFSET_PROPERTY,
      cssPixels(group.startOffset),
    );
    group.element.style.setProperty(
      AND_GROUP_END_OFFSET_PROPERTY,
      cssPixels(group.endOffset),
    );
  });
  return packed;
}
